import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { FiDollarSign, FiClock, FiAlertCircle, FiTrendingUp, FiCalendar, FiMapPin, FiRefreshCw } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { getReportData, fetchExchangeRates, FREQUENCY_LABELS } from '../../services/billing';
import { convertAmount } from '../../services/exchangeRates';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const DISPLAY_CURRENCIES = [
  { code: 'SGD', label: 'S$ SGD' },
  { code: 'LKR', label: 'Rs LKR' },
  { code: 'USD', label: '$ USD' },
  { code: 'EUR', label: '€ EUR' },
  { code: 'GBP', label: '£ GBP' },
  { code: 'AUD', label: 'A$ AUD' },
  { code: 'INR', label: '₹ INR' },
  { code: 'JPY', label: '¥ JPY' },
  { code: 'MYR', label: 'RM MYR' },
  { code: 'THB', label: '฿ THB' },
  { code: 'IDR', label: 'Rp IDR' },
  { code: 'AED', label: 'د.إ AED' }
];

const CURRENCY_SYMBOLS = {
  SGD: 'S$', LKR: 'Rs', USD: '$', EUR: '€', GBP: '£', AUD: 'A$',
  INR: '₹', JPY: '¥', MYR: 'RM', THB: '฿', IDR: 'Rp', AED: 'د.إ',
  CAD: 'C$', CHF: 'CHF', CNY: '¥', KRW: '₩', PHP: '₱', HKD: 'HK$',
  TWD: 'NT$', NZD: 'NZ$', ZAR: 'R', BRL: 'R$', MXN: 'Mex$',
  SAR: 'ر.س', PKR: 'Rs', BDT: '৳', VND: '₫'
};

function formatAmount(amount, currency) {
  const sym = CURRENCY_SYMBOLS[currency] || currency;
  return `${sym} ${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDate(dateString) {
  const date = new Date(dateString + 'T00:00:00');
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatDateTime(isoString) {
  return new Date(isoString).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
  });
}

export default function RevenueReport({ countries }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [countryFilter, setCountryFilter] = useState('all');
  const [displayCurrency, setDisplayCurrency] = useState('SGD');
  const [data, setData] = useState(null);
  const [rates, setRates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const currentYear = now.getFullYear();
  const years = Array.from({ length: Math.max(1, currentYear - 2025) }, (_, i) => 2026 + i);

  // Build country labels/flags from dynamic countries list
  const countryLabels = {};
  const countryFlags = {};
  (countries || []).forEach(c => {
    countryLabels[c.code] = c.name;
    countryFlags[c.code] = c.flag;
  });

  useEffect(() => {
    fetchExchangeRates().then(setRates).catch(() => {});
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    const request = month === 0
      ? Promise.all(MONTHS.map((_, index) => getReportData(year, index + 1, countryFilter))).then(monthly => ({
        rawPayments: monthly.flatMap(result => result.rawPayments.map(payment => ({
          ...payment,
          id: `${payment.id}-${payment.due_date}-${payment.paid_at}-${payment.amount}`
        }))),
        rawOutstanding: monthly.flatMap(result => result.rawOutstanding),
        rawOverdue: monthly[monthly.length - 1].rawOverdue
      }))
      : getReportData(year, month, countryFilter);
    request
      .then(result => { if (active) setData(result); })
      .catch(err => { if (active) setError(err.message); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [year, month, countryFilter]);

  // Convert all amounts to display currency
  const converted = useMemo(() => {
    if (!data || !rates) return null;

    const conv = (amount, fromCurrency) => convertAmount(amount, fromCurrency, displayCurrency, rates);

    const totalCollected = data.rawPayments.reduce((s, p) => s + conv(Number(p.amount), p.currency), 0);

    const outstanding = data.rawOutstanding.map(c => ({
      ...c,
      convertedAmount: conv(Number(c.monthly_amount), c.currency)
    }));
    const totalOutstanding = outstanding.reduce((s, c) => s + c.convertedAmount, 0);

    const overdue = data.rawOverdue.map(c => ({
      ...c,
      convertedAmount: conv(Number(c.monthly_amount), c.currency)
    }));
    const totalOverdue = overdue.reduce((s, c) => s + c.convertedAmount, 0);

    const payments = data.rawPayments.map(p => ({
      ...p,
      convertedAmount: conv(Number(p.amount), p.currency)
    }));

    const expected = totalCollected + totalOutstanding;
    const collectionRate = expected > 0 ? (totalCollected / expected) * 100 : 0;

    return {
      payments,
      totalCollected,
      outstanding,
      totalOutstanding,
      overdue,
      totalOverdue,
      collectionRate,
      paymentCount: payments.length
    };
  }, [data, rates, displayCurrency]);

  const cards = converted ? [
    {
      label: 'Collected',
      value: formatAmount(converted.totalCollected, displayCurrency),
      detail: `${converted.paymentCount} payment${converted.paymentCount !== 1 ? 's' : ''}`,
      icon: FiDollarSign,
      gradient: 'from-emerald-500 to-teal-500',
      bg: 'bg-emerald-50',
      text: 'text-emerald-600'
    },
    {
      label: 'Outstanding',
      value: formatAmount(converted.totalOutstanding, displayCurrency),
      detail: `${converted.outstanding.length} client${converted.outstanding.length !== 1 ? 's' : ''} unpaid this month`,
      icon: FiClock,
      gradient: 'from-amber-500 to-orange-500',
      bg: 'bg-amber-50',
      text: 'text-amber-600'
    },
    {
      label: 'Total Overdue',
      value: formatAmount(converted.totalOverdue, displayCurrency),
      detail: `${converted.overdue.length} overdue across all months`,
      icon: FiAlertCircle,
      gradient: 'from-red-500 to-rose-500',
      bg: 'bg-red-50',
      text: 'text-red-600'
    },
    {
      label: 'Collection Rate',
      value: `${converted.collectionRate.toFixed(1)}%`,
      detail: 'Of expected this month',
      icon: FiTrendingUp,
      gradient: 'from-blue-500 to-indigo-500',
      bg: 'bg-blue-50',
      text: 'text-blue-600'
    }
  ] : [];

  return (
    <div className="space-y-6">
      {/* Filters Row 1: Period + Country */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <SafeIcon icon={FiCalendar} className="w-4 h-4 text-slate-400" />
          <span className="text-sm font-semibold text-slate-600">Period:</span>
        </div>
        <select
          value={month}
          onChange={e => setMonth(Number(e.target.value))}
          className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
        >
          <option value={0}>All</option>
          {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
        </select>
        <select
          value={year}
          onChange={e => setYear(Number(e.target.value))}
          className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
        >
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </select>

        <div className="h-6 w-px bg-slate-200 hidden sm:block" />

        <div className="flex items-center gap-2">
          <SafeIcon icon={FiMapPin} className="w-4 h-4 text-slate-400" />
          <span className="text-sm font-semibold text-slate-600">Country:</span>
        </div>
        <div className="flex rounded-xl border border-slate-200 bg-white p-1 shadow-sm">
          <button
            onClick={() => setCountryFilter('all')}
            className={`rounded-lg px-3.5 py-1.5 text-sm font-semibold transition-all ${countryFilter === 'all' ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          >
            All
          </button>
          {(countries || []).filter(c => c.is_active).map(c => (
            <button
              key={c.code}
              onClick={() => setCountryFilter(c.code)}
              className={`rounded-lg px-3.5 py-1.5 text-sm font-semibold transition-all flex items-center gap-1.5 ${countryFilter === c.code ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            >
              <span className="text-base">{c.flag}</span>
              {c.name}
            </button>
          ))}
        </div>
      </div>

      {/* Filters Row 2: Display Currency */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <SafeIcon icon={FiRefreshCw} className="w-4 h-4 text-slate-400" />
          <span className="text-sm font-semibold text-slate-600">Display in:</span>
        </div>
        <select
          value={displayCurrency}
          onChange={e => setDisplayCurrency(e.target.value)}
          className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
        >
          {DISPLAY_CURRENCIES.map(c => (
            <option key={c.code} value={c.code}>{c.label}</option>
          ))}
        </select>
        {rates && displayCurrency !== 'USD' && (
          <span className="text-xs text-slate-400 flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
            1 USD = {rates[displayCurrency]?.toLocaleString(undefined, { maximumFractionDigits: 4 })} {displayCurrency}
          </span>
        )}
        <span className="text-sm text-slate-400 ml-auto">
          Showing: <strong className="text-slate-700">{month === 0 ? `All of ${year}` : `${MONTHS[month - 1]} ${year}`}</strong>
          {countryFilter !== 'all' && (
            <> in <strong className="text-slate-700">{countryLabels[countryFilter] || countryFilter}</strong></>
          )}
        </span>
      </div>

      {error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          <SafeIcon icon={FiAlertCircle} className="inline w-4 h-4 mr-2" />{error}
        </div>
      )}

      {loading || !converted ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {cards.map((card, i) => {
              const Icon = card.icon;
              return (
                <motion.div
                  key={card.label}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.08 }}
                  className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className={`flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br ${card.gradient} shadow-lg`}>
                      <SafeIcon icon={Icon} className="w-4 h-4 text-white" />
                    </div>
                    <span className={`text-[11px] font-bold uppercase tracking-wider ${card.text}`}>{card.label}</span>
                  </div>
                  <p className="text-2xl font-bold tracking-tight text-slate-900">{card.value}</p>
                  <p className="mt-1 text-xs text-slate-500">{card.detail}</p>
                  <div className={`absolute -right-3 -bottom-3 h-20 w-20 rounded-full ${card.bg} opacity-50`} />
                </motion.div>
              );
            })}
          </div>

          {/* Collection Rate Bar */}
          {converted.collectionRate > 0 && (
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-slate-700">Monthly Collection Progress</span>
                <span className="text-sm font-bold text-blue-600">{converted.collectionRate.toFixed(1)}%</span>
              </div>
              <div className="h-3 rounded-full bg-slate-100 overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${Math.min(converted.collectionRate, 100)}%` }}
                  transition={{ duration: 0.8, ease: 'easeOut' }}
                  className="h-full rounded-full bg-gradient-to-r from-blue-500 to-indigo-500"
                />
              </div>
            </div>
          )}

          {/* Payment History */}
          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
            <div className="p-5 border-b border-slate-100">
              <h3 className="font-bold text-slate-900">Payment History</h3>
              <p className="text-xs text-slate-500 mt-0.5">
                {converted.payments.length} payment{converted.payments.length !== 1 ? 's' : ''} in {month === 0 ? `all of ${year}` : `${MONTHS[month - 1]} ${year}`}
                {countryFilter !== 'all' && ` (${countryLabels[countryFilter] || countryFilter})`}
                {displayCurrency !== 'SGD' && ` — converted to ${displayCurrency}`}
              </p>
            </div>
            {converted.payments.length === 0 ? (
              <div className="p-10 text-center text-sm text-slate-400">No payments recorded for this period</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-100 text-left text-xs font-semibold uppercase tracking-wider text-slate-400">
                      <th className="px-5 py-3">Client</th>
                      <th className="px-5 py-3">Original</th>
                      <th className="px-5 py-3">Converted</th>
                      <th className="px-5 py-3">Frequency</th>
                      <th className="px-5 py-3">Due Date</th>
                      <th className="px-5 py-3">Paid On</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {converted.payments.map(p => (
                      <tr key={p.id} className="hover:bg-slate-50/50 transition-colors">
                        <td className="px-5 py-3 font-semibold text-slate-900">{p.client_name}</td>
                        <td className="px-5 py-3 text-slate-500">
                          {formatAmount(Number(p.amount), p.currency)}
                        </td>
                        <td className="px-5 py-3 font-medium text-emerald-600">
                          {formatAmount(p.convertedAmount, displayCurrency)}
                        </td>
                        <td className="px-5 py-3 text-slate-500">{FREQUENCY_LABELS[p.billing_frequency] || p.billing_frequency}</td>
                        <td className="px-5 py-3 text-slate-500">{formatDate(p.due_date)}</td>
                        <td className="px-5 py-3 text-slate-500">{formatDateTime(p.paid_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Overdue Clients */}
          <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
            <div className="p-5 border-b border-slate-100">
              <h3 className="font-bold text-slate-900">Currently Overdue</h3>
              <p className="text-xs text-slate-500 mt-0.5">
                {converted.overdue.length} client{converted.overdue.length !== 1 ? 's' : ''} past due across all months
                {countryFilter !== 'all' && ` (${countryLabels[countryFilter] || countryFilter})`}
              </p>
            </div>
            {converted.overdue.length === 0 ? (
              <div className="p-10 text-center text-sm text-slate-400">
                <span className="text-emerald-500">✓</span> No overdue clients — all payments are up to date
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-100 text-left text-xs font-semibold uppercase tracking-wider text-slate-400">
                      <th className="px-5 py-3">Client</th>
                      <th className="px-5 py-3">Original</th>
                      <th className="px-5 py-3">Converted</th>
                      <th className="px-5 py-3">Due Date</th>
                      <th className="px-5 py-3">Days Overdue</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {converted.overdue.map(c => (
                      <tr key={c.id} className="hover:bg-red-50/30 transition-colors">
                        <td className="px-5 py-3 font-semibold text-slate-900">{c.client_name}</td>
                        <td className="px-5 py-3 text-slate-500">
                          {formatAmount(Number(c.monthly_amount), c.currency)}
                        </td>
                        <td className="px-5 py-3 font-medium text-red-600">
                          {formatAmount(c.convertedAmount, displayCurrency)}
                        </td>
                        <td className="px-5 py-3 text-slate-500">{formatDate(c.next_due_date)}</td>
                        <td className="px-5 py-3">
                          <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-bold ${c.daysOverdue > 30 ? 'bg-red-100 text-red-700' : c.daysOverdue > 7 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>
                            {c.daysOverdue} day{c.daysOverdue !== 1 ? 's' : ''}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}